import { useEffect, useMemo, useRef, useState } from "react";
import ReportTable from "../ReportTable.component";
import ReportService from "../../../services/reports/Report.service";
import CustomReportService from "../../../services/reports/CustomReport.service";
import EpochMonthService from "../../../services/EpochMonth.service";
import { BaseReportParsedData, MoneyDataItem } from "../../../interfaces/reports/BaseReport.interface";
import * as XLSX from "xlsx";
import MultiSelect from "../../../tailwindComponents/ui/multi-select";
import { clientMapping as mrfDefaultSortsByClient } from "../classPerformance/customSorts/MRFDefaultSortsByClient";
import { classOrder as eccoClassOrder, getClassOrderIndex } from "../classPerformance/customSorts/EccoClothesCustomSort";

interface MRFReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

// Raw record based on GetMRFCustomReportData.sql final SELECT
export interface MRFCustomRaw {
    clientId: number;
    locationclassid: number;
    epochmonthrm: number;
    epochmonthspan: number | null;
    fiscalstartepoch: number | null;
    sales: number | null;
    inventorybomlm: number | null;
    inventoryeomlm: number | null;
    inventorydeviationrm: number | null;
    inventorydeviationcostrm: number | null;
    salesplanrm: number | null;
    salesrm: number | null;
    inventorybomplanrm: number | null;
    saleslm: number | null;
    salesly: number | null;
    salesplanlm: number | null;
    inventoryeomly: number | null;
    // Added fields from SQL
    inventorybomrm: number | null;
    inventorybomly: number | null;
    salesfiscalmtd: number | null;
    salesfiscally: number | null;
    inflowplanretail: number | null;
    inflowplancost: number | null;
    inflowplanretailrm: number | null;
    inflowplancostrm: number | null;
    onorderretaileom: number | null;
    onordercosteom: number | null;
    onorderretaileomcurrent: number | null;
    onordercosteomcurrent: number | null;
    otbretaileom: number | null;
    otbcosteom: number | null;
    otbretaileomcurrent: number | null;
    otbcosteomcurrent: number | null;

    // CSV offset fields returned by the SQL (ordered: epochMonth, epochMonth+1, ...)
    inflowPlanRetailOffsetCSV?: string | null;
    inflowPlanCostOffsetCSV?: string | null;
    onOrderRetailEOMOffsetCSV?: string | null;
    onOrderCostEOMOffsetCSV?: string | null;

    // hierarchy fields
    clientCode: string;
    clientName: string;
    locationId: number | null;
    locationCode: string;
    locationName: string;
    classCode: string;
    className: string;
    category: string | null;
    categorySortOrder: number | null;
    categoryId: number | null;
}

// Parsed type compatible with ReportService.groupByHierarchy
interface MRFParsedData extends BaseReportParsedData {
    sales: MoneyDataItem;
    salesRM: MoneyDataItem;
    salesPlanRM: MoneyDataItem;
    salesFiscalMTD: MoneyDataItem;
    salesFiscalLY: MoneyDataItem;
    invBomLM: MoneyDataItem;
    invEomLM: MoneyDataItem;
    invDeviationRetail: MoneyDataItem;
    invDeviationCost: MoneyDataItem;
    invBomPlanRM: MoneyDataItem;
    invBomRM: MoneyDataItem;
    invBomLY: MoneyDataItem;
    salesLM: MoneyDataItem;
    salesLY: MoneyDataItem;
    salesPlanLM: MoneyDataItem;
    invEomLY: MoneyDataItem;
    inflowPlanRetail: MoneyDataItem; // all spans
    inflowPlanCost: MoneyDataItem;   // all spans
    inflowPlanRetailRM: MoneyDataItem; // span 0
    inflowPlanCostRM: MoneyDataItem;   // span 0
    onOrderRetailCurrent: MoneyDataItem;
    onOrderCostCurrent: MoneyDataItem;
    otbRetailCurrent: MoneyDataItem;
    otbCostCurrent: MoneyDataItem;

    // Inflow Offsets 1-9 (retail/cost)
    offsetRetail1: MoneyDataItem; offsetRetail2: MoneyDataItem; offsetRetail3: MoneyDataItem; offsetRetail4: MoneyDataItem; offsetRetail5: MoneyDataItem; offsetRetail6: MoneyDataItem;
    offsetRetail7: MoneyDataItem; offsetRetail8: MoneyDataItem; offsetRetail9: MoneyDataItem;
    offsetCost1: MoneyDataItem; offsetCost2: MoneyDataItem; offsetCost3: MoneyDataItem; offsetCost4: MoneyDataItem; offsetCost5: MoneyDataItem; offsetCost6: MoneyDataItem;
    offsetCost7: MoneyDataItem; offsetCost8: MoneyDataItem; offsetCost9: MoneyDataItem;

    // On Order Offsets 1-9 (retail/cost)
    onOrderRetailOffset1: MoneyDataItem; onOrderRetailOffset2: MoneyDataItem; onOrderRetailOffset3: MoneyDataItem; onOrderRetailOffset4: MoneyDataItem; onOrderRetailOffset5: MoneyDataItem; onOrderRetailOffset6: MoneyDataItem;
    onOrderRetailOffset7: MoneyDataItem; onOrderRetailOffset8: MoneyDataItem; onOrderRetailOffset9: MoneyDataItem;
    onOrderCostOffset1: MoneyDataItem; onOrderCostOffset2: MoneyDataItem; onOrderCostOffset3: MoneyDataItem; onOrderCostOffset4: MoneyDataItem; onOrderCostOffset5: MoneyDataItem; onOrderCostOffset6: MoneyDataItem;
    onOrderCostOffset7: MoneyDataItem; onOrderCostOffset8: MoneyDataItem; onOrderCostOffset9: MoneyDataItem;

    // OTB Offsets 1-9 (retail/cost)
    otbRetailOffset1: MoneyDataItem; otbRetailOffset2: MoneyDataItem; otbRetailOffset3: MoneyDataItem; otbRetailOffset4: MoneyDataItem; otbRetailOffset5: MoneyDataItem; otbRetailOffset6: MoneyDataItem;
    otbRetailOffset7: MoneyDataItem; otbRetailOffset8: MoneyDataItem; otbRetailOffset9: MoneyDataItem;
    otbCostOffset1: MoneyDataItem; otbCostOffset2: MoneyDataItem; otbCostOffset3: MoneyDataItem; otbCostOffset4: MoneyDataItem; otbCostOffset5: MoneyDataItem; otbCostOffset6: MoneyDataItem;
    otbCostOffset7: MoneyDataItem; otbCostOffset8: MoneyDataItem; otbCostOffset9: MoneyDataItem;
}

const HIERARCHY = ["category", "classCode"] as const;

type ValueMode = "retail" | "cost";

type ClassSortMode = "alphanumeric" | "trailingLettersLast" | "noLetterClasses";

function emptyInflowOffsets(): Record<`offsetRetail${number}` | `offsetCost${number}`, MoneyDataItem> {
    return {
        offsetRetail1: new MoneyDataItem(0), offsetRetail2: new MoneyDataItem(0), offsetRetail3: new MoneyDataItem(0), offsetRetail4: new MoneyDataItem(0), offsetRetail5: new MoneyDataItem(0), offsetRetail6: new MoneyDataItem(0),
        offsetRetail7: new MoneyDataItem(0), offsetRetail8: new MoneyDataItem(0), offsetRetail9: new MoneyDataItem(0),
        offsetCost1: new MoneyDataItem(0), offsetCost2: new MoneyDataItem(0), offsetCost3: new MoneyDataItem(0), offsetCost4: new MoneyDataItem(0), offsetCost5: new MoneyDataItem(0), offsetCost6: new MoneyDataItem(0),
        offsetCost7: new MoneyDataItem(0), offsetCost8: new MoneyDataItem(0), offsetCost9: new MoneyDataItem(0),
    } as any;
}

function emptyOnOrderOffsets(): Record<`onOrderRetailOffset${number}` | `onOrderCostOffset${number}`, MoneyDataItem> {
    return {
        onOrderRetailOffset1: new MoneyDataItem(0), onOrderRetailOffset2: new MoneyDataItem(0), onOrderRetailOffset3: new MoneyDataItem(0), onOrderRetailOffset4: new MoneyDataItem(0), onOrderRetailOffset5: new MoneyDataItem(0), onOrderRetailOffset6: new MoneyDataItem(0),
        onOrderRetailOffset7: new MoneyDataItem(0), onOrderRetailOffset8: new MoneyDataItem(0), onOrderRetailOffset9: new MoneyDataItem(0),
        onOrderCostOffset1: new MoneyDataItem(0), onOrderCostOffset2: new MoneyDataItem(0), onOrderCostOffset3: new MoneyDataItem(0), onOrderCostOffset4: new MoneyDataItem(0), onOrderCostOffset5: new MoneyDataItem(0), onOrderCostOffset6: new MoneyDataItem(0),
        onOrderCostOffset7: new MoneyDataItem(0), onOrderCostOffset8: new MoneyDataItem(0), onOrderCostOffset9: new MoneyDataItem(0),
    } as any;
}

function emptyOtbOffsets(): Record<`otbRetailOffset${number}` | `otbCostOffset${number}`, MoneyDataItem> {
    return {
        otbRetailOffset1: new MoneyDataItem(0), otbRetailOffset2: new MoneyDataItem(0), otbRetailOffset3: new MoneyDataItem(0), otbRetailOffset4: new MoneyDataItem(0), otbRetailOffset5: new MoneyDataItem(0), otbRetailOffset6: new MoneyDataItem(0),
        otbRetailOffset7: new MoneyDataItem(0), otbRetailOffset8: new MoneyDataItem(0), otbRetailOffset9: new MoneyDataItem(0),
        otbCostOffset1: new MoneyDataItem(0), otbCostOffset2: new MoneyDataItem(0), otbCostOffset3: new MoneyDataItem(0), otbCostOffset4: new MoneyDataItem(0), otbCostOffset5: new MoneyDataItem(0), otbCostOffset6: new MoneyDataItem(0),
        otbCostOffset7: new MoneyDataItem(0), otbCostOffset8: new MoneyDataItem(0), otbCostOffset9: new MoneyDataItem(0),
    } as any;
}

function mapOffsetValue(prefix: 'offsetRetail' | 'offsetCost' | 'onOrderRetailOffset' | 'onOrderCostOffset' | 'otbRetailOffset' | 'otbCostOffset', span: number | null | undefined, value: number | null | undefined) {
    const m: any = {};
    const n = Number(span || 0);
    if (n >= 1 && n <= 9) {
        m[`${prefix}${n}`] = new MoneyDataItem(Number(value || 0));
    }
    return m as any;
}

function getNum<T extends Record<string, any>>(row: T, ...keys: string[]): number {
    for (const k of keys) {
        const v = row[k];
        if (v !== undefined && v !== null) return Number(v) || 0;
    }
    return 0;
}

function parseRaw(rows: MRFCustomRaw[]): MRFParsedData[] {
    return (rows || []).map((r) => {
        const span = getNum(r as any, 'epochmonthspan', 'epochMonthspan', 'epochMonthSpan');
        const inflowRetail = getNum(r as any, 'inflowplanretail', 'inflowPlanRetail');
        const inflowCost = getNum(r as any, 'inflowplancost', 'inflowPlanCost');

        // parse CSV offset strings (ordered starting at report month (index 0) then +1..+9)
        const parseCsv = (s?: string | null) => {
            if (!s) return [] as number[];
            return s.split(',').map(x => Number((x || '').trim()) || 0);
        };

        const inflowRetailArr = parseCsv((r as any).inflowPlanRetailOffsetCSV || (r as any).inflowplanretailoffsetcsv);
        const inflowCostArr = parseCsv((r as any).inflowPlanCostOffsetCSV || (r as any).inflowplancostoffsetcsv);
        const onOrderRetailArr = parseCsv((r as any).onOrderRetailEOMOffsetCSV || (r as any).onorderretaileomoffsetcsv);
        const onOrderCostArr = parseCsv((r as any).onOrderCostEOMOffsetCSV || (r as any).onordercosteomoffsetcsv);

        const ensureLen = (arr: number[], len = 10) => {
            const a = arr.slice(0);
            while (a.length < len) a.push(0);
            return a;
        };

        const infR = ensureLen(inflowRetailArr);
        const infC = ensureLen(inflowCostArr);
        const onR = ensureLen(onOrderRetailArr);
        const onC = ensureLen(onOrderCostArr);

        // Build offsets objects from CSV: index 0 is current month, indexes 1..9 map to Offset1..Offset9
        const inflowOffsets = { ...emptyInflowOffsets() } as any;
        const onOrderOffsets = { ...emptyOnOrderOffsets() } as any;
        const otbOffsets = { ...emptyOtbOffsets() } as any;

        for (let i = 1; i <= 9; i++) {
            inflowOffsets[`offsetRetail${i}`] = new MoneyDataItem(infR[i] || 0);
            inflowOffsets[`offsetCost${i}`] = new MoneyDataItem(infC[i] || 0);

            onOrderOffsets[`onOrderRetailOffset${i}`] = new MoneyDataItem(onR[i] || 0);
            onOrderOffsets[`onOrderCostOffset${i}`] = new MoneyDataItem(onC[i] || 0);

            const otbRetailVal = (infR[i] || 0) - (onR[i] || 0);
            const otbCostVal = (infC[i] || 0) - (onC[i] || 0);
            otbOffsets[`otbRetailOffset${i}`] = new MoneyDataItem(otbRetailVal);
            otbOffsets[`otbCostOffset${i}`] = new MoneyDataItem(otbCostVal);
        }

        // current-month ON ORDER / OTB values are provided separately in the row (from BaseAgg)
        const onOrderRetailCurrent = getNum(r as any, 'onorderretaileomcurrent', 'onOrderRetailEOMCurrent', 'onorderretaileom');
        const onOrderCostCurrent = getNum(r as any, 'onordercosteomcurrent', 'onOrderCostEOMCurrent', 'onordercosteom');

        return {
        locationCode: (r as any).locationCode,
        locationName: (r as any).locationName,
        classCode: (r as any).classCode,
        className: (r as any).className,
        category: (r as any).category ?? "N/A",
        categorySortOrder: r.categorySortOrder ?? 9999,
        parentClientClassCode: null,

        sales: new MoneyDataItem(getNum(r as any, 'sales', 'sales')),
        salesRM: new MoneyDataItem(getNum(r as any, 'salesrm', 'salesRM')),
        salesPlanRM: new MoneyDataItem(getNum(r as any, 'salesplanrm', 'salesPlanRM')),
        salesFiscalMTD: new MoneyDataItem(getNum(r as any, 'salesfiscalmtd', 'salesFiscalMTD')),
        salesFiscalLY: new MoneyDataItem(getNum(r as any, 'salesfiscally', 'salesFiscalLY')),
        invBomLM: new MoneyDataItem(getNum(r as any, 'inventorybomlm', 'inventoryBomLM')),
        invEomLM: new MoneyDataItem(getNum(r as any, 'inventoryeomlm', 'inventoryEomLM', 'invenotryeomlm')),
        invDeviationRetail: new MoneyDataItem(getNum(r as any, 'inventorydeviationrm', 'inventoryDeviationRM')),
        invDeviationCost: new MoneyDataItem(getNum(r as any, 'inventorydeviationcostrm', 'inventoryDeviationCostRM')),
        invBomPlanRM: new MoneyDataItem(getNum(r as any, 'inventorybomplanrm', 'inventoryBomPlanRM')),
        invBomRM: new MoneyDataItem(getNum(r as any, 'inventorybomrm', 'inventoryBomRM')),
        invBomLY: new MoneyDataItem(getNum(r as any, 'inventorybomly', 'inventoryBomLY')),
        salesLM: new MoneyDataItem(getNum(r as any, 'saleslm', 'salesLM')),
        salesLY: new MoneyDataItem(getNum(r as any, 'salesly', 'salesLY')),
        salesPlanLM: new MoneyDataItem(getNum(r as any, 'salesplanlm', 'salesPlanLM')),
        invEomLY: new MoneyDataItem(getNum(r as any, 'inventoryeomly', 'inventoryEomLY')),
        inflowPlanRetail: new MoneyDataItem(inflowRetail),
        inflowPlanCost: new MoneyDataItem(inflowCost),
        inflowPlanRetailRM: new MoneyDataItem(getNum(r as any, 'inflowplanretailrm', 'inflowPlanRetailRM')),
        inflowPlanCostRM: new MoneyDataItem(getNum(r as any, 'inflowplancostrm', 'inflowPlanCostRM')),
        onOrderRetailCurrent: new MoneyDataItem(onOrderRetailCurrent),
        onOrderCostCurrent: new MoneyDataItem(onOrderCostCurrent),
        otbRetailCurrent: new MoneyDataItem(getNum(r as any, 'otbretaileomcurrent', 'otbRetailEOMCurrent')),
        otbCostCurrent: new MoneyDataItem(getNum(r as any, 'otbcosteomcurrent', 'otbCostEOMCurrent')),

        // offsets (from CSV)
        ...inflowOffsets,
        ...onOrderOffsets,
        ...otbOffsets,
        } as MRFParsedData;
    });
}

const MRFReport = ({ clientId, epochMonth }: MRFReportProps) => {
    const raw = useRef<MRFCustomRaw[]>([]);
    const [parsed, setParsed] = useState<MRFParsedData[]>([]);
    const [grouped, setGrouped] = useState<MRFParsedData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // filters
    const [locationFilter, setLocationFilter] = useState<string[]>(["ALL"]);
    const [mode, setMode] = useState<ValueMode>("retail");
    const [classSortMode, setClassSortMode] = useState<ClassSortMode>("alphanumeric");

    const clientIdNum = Number(clientId);
    const isOrbit719 = clientIdNum === 3044;

    useEffect(() => {
        // ORBIT719 always uses custom ordering; prevent mapped overrides from changing it.
        if (clientIdNum === 3044) {
            setClassSortMode('alphanumeric');
            return;
        }

        // Apply per-client default class sort, if configured (mapping uses numeric clientId)
        const mapped = (mrfDefaultSortsByClient as any)?.[clientIdNum] as ClassSortMode | undefined;
        if (mapped) setClassSortMode(mapped);
    }, [clientIdNum]);

    useEffect(() => {
        const load = async () => {
        setLoading(true);
        try {
            const data = await CustomReportService.getMRFCustomReportData({ clientId, epochMonth: epochMonth ?? EpochMonthService.getTodayEpochMonth() });
            raw.current = (data as MRFCustomRaw[]) || [];
            const _parsed = parseRaw(raw.current);
            setParsed(_parsed);
            const _grouped = ReportService.groupByHierarchy(_parsed, [...HIERARCHY]);
            setGrouped(_grouped);
        } catch (e) {
            setParsed([]);
            setGrouped([]);
        } finally {
            setLoading(false);
        }
        };

        load();
    }, [clientId, epochMonth]);

    const locations = useMemo(() => {
        const set = new Map<string, string>();
        parsed.forEach(p => {
        if (p.locationCode) set.set(p.locationCode, `${p.locationCode} ${p.locationName}`);
        });
        return [{ code: "ALL", label: "All Locations" }, ...Array.from(set.entries()).map(([code, label]) => ({ code, label }))];
    }, [parsed]);

    const columns = useMemo(() => {
        const hierarchyColumns = HIERARCHY.map(() => ({ name: "", sortable: false }));
        return [
        ...hierarchyColumns,
        { name: "Sales Fiscal MTD", sortable: false },
        { name: "Sales Plan Current Month", sortable: false },
        { name: "Sales Last Month", sortable: false },
        { name: "Sales Last Year", sortable: false },
        { name: "%L", sortable: false },
        { name: "Sales Plan Last Month", sortable: false },
        { name: "Inv BOM Last Year", sortable: false },
        { name: "P", sortable: false },
        { name: "Inv BOM Plan Current Month", sortable: false },
        { name: "Inv BOM", sortable: false },
        { name: "Inflow Plan + Deviation", sortable: false },
        // Inflow Offsets 1-9
        { name: "Inflow Plan Offset 1", sortable: false },
        { name: "Inflow Plan Offset 2", sortable: false },
        { name: "Inflow Plan Offset 3", sortable: false },
        { name: "Inflow Plan Offset 4", sortable: false },
        { name: "Inflow Plan Offset 5", sortable: false },
        { name: "Inflow Plan Offset 6", sortable: false },
        { name: "Inflow Plan Offset 7", sortable: false },
        { name: "Inflow Plan Offset 8", sortable: false },
        { name: "Inflow Plan Offset 9", sortable: false },
        // On Order (current + 1..9)
        { name: "On Order Current", sortable: false },
        { name: "On Order Offset 1", sortable: false },
        { name: "On Order Offset 2", sortable: false },
        { name: "On Order Offset 3", sortable: false },
        { name: "On Order Offset 4", sortable: false },
        { name: "On Order Offset 5", sortable: false },
        { name: "On Order Offset 6", sortable: false },
        { name: "On Order Offset 7", sortable: false },
        { name: "On Order Offset 8", sortable: false },
        { name: "On Order Offset 9", sortable: false },
        // OTB (current + 1..9)
        { name: "OTB Current Month", sortable: false },
        { name: "OTB Offset 1", sortable: false },
        { name: "OTB Offset 2", sortable: false },
        { name: "OTB Offset 3", sortable: false },
        { name: "OTB Offset 4", sortable: false },
        { name: "OTB Offset 5", sortable: false },
        { name: "OTB Offset 6", sortable: false },
        { name: "OTB Offset 7", sortable: false },
        { name: "OTB Offset 8", sortable: false },
        { name: "OTB Offset 9", sortable: false },
        // Cumulative OTB (current + 1..9)
        { name: "Cum. OTB Current Month", sortable: false },
        { name: "Cum. OTB Offset 1", sortable: false },
        { name: "Cum. OTB Offset 2", sortable: false },
        { name: "Cum. OTB Offset 3", sortable: false },
        { name: "Cum. OTB Offset 4", sortable: false },
        { name: "Cum. OTB Offset 5", sortable: false },
        { name: "Cum. OTB Offset 6", sortable: false },
        { name: "Cum. OTB Offset 7", sortable: false },
        { name: "Cum. OTB Offset 8", sortable: false },
        { name: "Cum. OTB Offset 9", sortable: false },
        // Month numbers
        { name: "Last Month", sortable: false },
        { name: "Report Month", sortable: false },
        { name: "Next Month", sortable: false },
        { name: "Next Month +1", sortable: false },
        { name: "Next Month +2", sortable: false },
        { name: "Next Month +3", sortable: false },
        { name: "Next Month +4", sortable: false },
        { name: "Next Month +5", sortable: false },
        { name: "Next Month +6", sortable: false },
        { name: "Next Month +7", sortable: false },
        { name: "Next Month +8", sortable: false },
        // Remaining calcs
        { name: "%BR", sortable: false },
        { name: "Sales Current", sortable: false },
        ];
    }, [mode]);

    const filtered = useMemo(() => {
        // Use ReportService.sortAndFilterData so filtering works correctly and preserves grouping/totals.
        // If 'ALL' is selected (or returned as default) treat as no filter (empty array)
        const locArr = (Array.isArray(locationFilter) && locationFilter.includes("ALL")) ? [] : (locationFilter || []);
        // class/category filters are not used in this component currently
        return ReportService.sortAndFilterData(parsed, [...HIERARCHY], locArr, [], []);
    }, [parsed, locationFilter]);

    const tableData = useMemo(() => {
        const epoch = epochMonth ?? EpochMonthService.getTodayEpochMonth();
        const monthNum = (em: number) => ((em % 12) + 12) % 12 + 1; // 1-12, safe for negatives

        const baseRows = filtered.map((r) => {
            const labels = ReportService.hierarchyItemToVerticalDisplayNames(r, [...HIERARCHY]);
            const salesFiscalMtd = Number(r.salesFiscalMTD?.value) || 0;
            const salesPlanCurrent = Number(r.salesPlanRM?.value) || 0;
            const salesLastMonth = Number(r.salesLM?.value) || 0;
            const salesLastYear = Number(r.salesLY?.value) || 0;
            const pctL = salesLastYear !== 0 ? ((salesLastMonth - salesLastYear) / Math.abs(salesLastYear)) * 100 : 0;
            const salesPlanLastMonth = Number(r.salesPlanLM?.value) || 0;
            const invBomLastYear = Number(r.invEomLY?.value) || 0;
            const pRatio = salesLastMonth !== 0 ? (Number(r.invBomLM?.value) || 0) / Math.abs(salesLastMonth) : 0;
            const invBomPlanCurrent = Number(r.invBomPlanRM?.value) || 0;
            const invBom = Number(r.invBomRM?.value) || 0;
            const deviation = mode === "retail" ? Number(r.invDeviationRetail?.value) || 0 : Number(r.invDeviationCost?.value) || 0;
            const inflowPlanRM = mode === "retail" ? Number(r.inflowPlanRetailRM?.value) || 0 : Number(r.inflowPlanCostRM?.value) || 0;
            const inflowPlanPlusDeviation = inflowPlanRM + deviation;

            // Inflow Offsets 1..9
            const inflowOffsets = Array.from({ length: 9 }, (_, i) => {
                const idx = i + 1;
                const key = mode === 'retail' ? (`offsetRetail${idx}` as const) : (`offsetCost${idx}` as const);
                const v = (r as any)[key]?.value ?? 0;
                return ReportService.formatCurrency(Number(v) || 0);
            });

            // On Order values
            const onOrderCurrent = mode === 'retail' ? (Number((r as any).onOrderRetailCurrent?.value) || 0) : (Number((r as any).onOrderCostCurrent?.value) || 0);
            const onOrderOffsets = Array.from({ length: 9 }, (_, i) => {
                const idx = i + 1;
                const key = mode === 'retail' ? (`onOrderRetailOffset${idx}` as const) : (`onOrderCostOffset${idx}` as const);
                const v = (r as any)[key]?.value ?? 0;
                return ReportService.formatCurrency(Number(v) || 0);
            });

            // OTB values
            const otbCurrentBase = mode === 'retail' ? (Number((r as any).otbRetailCurrent?.value) || 0) : (Number((r as any).otbCostCurrent?.value) || 0);
            const otbCurrent = otbCurrentBase + deviation; // add deviation for current month
            const otbOffsetsVals = Array.from({ length: 9 }, (_, i) => {
                const idx = i + 1;
                const key = mode === 'retail' ? (`otbRetailOffset${idx}` as const) : (`otbCostOffset${idx}` as const);
                const v = (r as any)[key]?.value ?? 0;
                return Number(v) || 0;
            });
            const otbOffsets = otbOffsetsVals.map(v => ReportService.formatCurrency(v));

            // Cum OTB
            const cum = [otbCurrent];
            for (let i = 0; i < otbOffsetsVals.length; i++) {
                cum.push(cum[cum.length - 1] + otbOffsetsVals[i]);
            }
            const cumFormatted = cum.map(v => ReportService.formatCurrency(v));

            // Month numbers
            const months = [
                monthNum((epoch as number) - 1), // Last Month
                monthNum(epoch as number), // Report Month
                monthNum((epoch as number) + 1),
                monthNum((epoch as number) + 2),
                monthNum((epoch as number) + 3),
                monthNum((epoch as number) + 4),
                monthNum((epoch as number) + 5),
                monthNum((epoch as number) + 6),
                monthNum((epoch as number) + 7),
                monthNum((epoch as number) + 8),
                monthNum((epoch as number) + 9),
            ].map(n => String(n));

            // %BR
            const salesFiscalLY = Number(r.salesFiscalLY?.value) || 0;
            const pctBR = salesFiscalLY !== 0 ? ((salesFiscalMtd - salesFiscalLY) / Math.abs(salesFiscalLY)) * 100 : 0;

            const salesCurrent = Number(r.salesRM?.value) || 0;

            return [
                ...labels,
                ReportService.formatCurrency(salesFiscalMtd),
                ReportService.formatCurrency(salesPlanCurrent),
                ReportService.formatCurrency(salesLastMonth),
                ReportService.formatCurrency(salesLastYear),
                ReportService.formatPercent(pctL),
                ReportService.formatCurrency(salesPlanLastMonth),
                ReportService.formatCurrency(invBomLastYear),
                (Math.round(pRatio * 10) / 10).toFixed(1),
                ReportService.formatCurrency(invBomPlanCurrent),
                ReportService.formatCurrency(invBom),
                ReportService.formatCurrency(inflowPlanPlusDeviation),
                ...inflowOffsets,
                ReportService.formatCurrency(onOrderCurrent),
                ...onOrderOffsets,
                ReportService.formatCurrency(otbCurrent),
                ...otbOffsets,
                ...cumFormatted,
                ...months,
                ReportService.formatPercent(pctBR),
                ReportService.formatCurrency(salesCurrent),
            ];
        });

        const hasAnyLetters = (code: string) => /[a-zA-Z]/.test((code || '').trim());

        const compareAlphanumeric = (a: string, b: string) =>
            (a || '').localeCompare((b || ''), undefined, { numeric: true, sensitivity: 'base' });

        // Updated behavior: ALL codes that contain letters sort after codes that are strictly numeric.
        // Within each bucket (numeric-only vs contains-letters), keep standard alphanumeric ordering.
        const compareLettersBucketLast = (a: string, b: string) => {
            const aHasLetters = hasAnyLetters(a);
            const bHasLetters = hasAnyLetters(b);
            if (aHasLetters !== bHasLetters) return aHasLetters ? 1 : -1;
            return compareAlphanumeric(a, b);
        };

        const compareClassCode = (a: string, b: string) => {
            switch (classSortMode) {
                case 'alphanumeric':
                    return compareAlphanumeric(a, b);
                case 'noLetterClasses':
                    // sorting is still standard; filtering done separately
                    return compareAlphanumeric(a, b);
                case 'trailingLettersLast':
                default:
                    return compareLettersBucketLast(a, b);
            }
        };

        const getCode = (row: Array<string | number>): string => String(row[1] || '').split(' ')[0];

        // Reorder within each Category group: keep TOTAL rows, then class rows sorted per classSortMode.
        // For ORBIT719, after base table is built, apply custom class order within each category group.
        // Optionally filter out letter-suffixed class codes for "noLetterClasses".
        const sortedRows: Array<Array<string | number>> = [];
        let i = 0;
        while (i < baseRows.length) {
            const currentCategory = baseRows[i]?.[0];
            const group: Array<Array<string | number>> = [];
            while (i < baseRows.length && baseRows[i]?.[0] === currentCategory) {
                group.push(baseRows[i]);
                i++;
            }

            if (typeof currentCategory === 'string' && currentCategory.toLowerCase() === 'total') {
                sortedRows.push(...group);
                continue;
            }

            const totals = group.filter(r => String(r[1] || '').toLowerCase() === 'total');
            let classes = group.filter(r => String(r[1] || '').toLowerCase() !== 'total');

            // IMPORTANT: For ORBIT719 we do NOT apply the "noLetterClasses" filter.
            // The custom order list includes lettered codes and must be respected as-is.
            if (!isOrbit719 && classSortMode === 'noLetterClasses') {
                // exclude *letter* classes
                classes = classes.filter(r => !hasAnyLetters(getCode(r)));
            }

            if (isOrbit719) {
                const order = eccoClassOrder || [];
                classes.sort((a, b) => {
                    const aCode = getCode(a);
                    const bCode = getCode(b);
                    const ai = getClassOrderIndex(aCode, order);
                    const bi = getClassOrderIndex(bCode, order);
                    if (ai !== bi) return ai - bi;
                    return compareAlphanumeric(aCode, bCode);
                });
            } else {
                classes.sort((a, b) => compareClassCode(getCode(a), getCode(b)));
            }

            sortedRows.push(...totals, ...classes);
        }

        return sortedRows;
    }, [filtered, mode, epochMonth, columns, classSortMode, clientIdNum, isOrbit719]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        return ReportService.getRowClassName(tableData[rowIndex], ["category", "classCode"]);
    };

    const exportToExcel = () => {
        try {
        const header = columns.map(c => (typeof c === 'string' ? c : c.name));
        const ws = XLSX.utils.aoa_to_sheet([header, ...tableData]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'MRF');
        const fileName = `MRF_${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())}.xlsx`;
        XLSX.writeFile(wb, fileName);
        } catch (e) {
        console.error(e);
        }
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
        <div className="w-full max-w-[1750px] mx-auto">
            <h2 className="text-2xl font-bold mb-2">
            {`${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())} MRF (Category by Class)`}
            </h2>
            <div className="w-full flex flex-wrap items-center justify-between px-4 mb-2 gap-2">
            <div className="flex items-center gap-2">
                <label className="text-sm">Location:</label>
                <MultiSelect
                  options={locations.map(l => ({ code: l.code, label: l.label }))}
                  value={locationFilter}
                  onChange={(v) => setLocationFilter(v)}
                  placeholder="All Locations"
                />
            </div>
            <div className="flex items-center gap-2">
                <label className="text-sm">Values:</label>
                <select
                className="h-9 px-2 border rounded-md text-sm"
                value={mode}
                onChange={(e) => setMode(e.target.value as ValueMode)}
                >
                <option value="retail">Retail</option>
                <option value="cost">Cost</option>
                </select>
            </div>
            {!isOrbit719 && (
                <div className="flex items-center gap-2">
                    <label className="text-sm">Class sort:</label>
                    <select
                    className="h-9 px-2 border rounded-md text-sm"
                    value={classSortMode}
                    title={isOrbit719 ? 'Custom class sort is enforced for this client' : undefined}
                    onChange={(e) => setClassSortMode(e.target.value as ClassSortMode)}
                    >
                    <option value="alphanumeric">Alphanumeric</option>
                    <option value="trailingLettersLast">Alphanumeric, number only then lettered</option>
                    <option value="noLetterClasses">Alphanumeric (exclude letter classes)</option>
                    </select>
                </div>
            )}
            <div className="flex-1" />
            <button
                onClick={exportToExcel}
                className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow transition"
            >
                Download Excel
            </button>
            </div>
            {loading ? (
            <p>Loading...</p>
            ) : (
            <div className="w-full h-full">
                <ReportTable
                    columns={columns}
                    data={tableData}
                    rowClassName={rowClassName}
                    headerClassName="bg-slate-700 !text-white !text-center py-1 px-1"
                    stickyHeader={true}
                    hierarchyDepth={HIERARCHY.length}
                />
            </div>
            )}
        </div>
        </div>
    );
};

export default MRFReport;